/** @jsxImportSource @emotion/react */
import React, { useMemo, useState, useEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Tab, Tabs } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ContainerLayout } from 'components/layouts/ContainerLayout';
import { tabs } from 'components/layouts/Tab';
import UtComparisonReportPage from './UtComparisonReportPage';
import UtComparisonElecReportPage from './elec/UtComparisonElecReportPage';
import AnalysisReportPanel from './report/AnalysisReportPanel';
import { usePageSubTitle } from 'hooks/usePageSubTitle';
import { getUtMatrixComparisonReportList } from 'apis/ut/UtMatrixComparisonReportList';
import { UtMatrixComparisonReportList } from '../../models/ut/UtMatrixComparisonReportList';
import { ChatbotButton } from 'components/buttons/ChatbotButton';
import { useChatbotStore } from 'stores/useChatbotStore';

interface PageLocationState {
  rptId?: string;
  rptNm?: string;
}
/**
 * @desc UT Matrix 정합화 Report 상세 — 기계/전기/분석 Report를 탭으로 함께 본다.
 *
 * 리포트가 기계/전기로 나뉘지 않고 하나(RPT_ID)로 저장되므로,
 * 목록의 '상세분석' 버튼에서 rptId만 받아 세 화면을 탭으로 노출한다.
 * 기계/전기 탭 컴포넌트는 location.state.rptId를 그대로 읽어 스냅샷을 복원한다.
 *
 * 탭 순서는 "기계 / 전기 / 분석 Report"다.
 * ⚠ ref/html 데모는 전기가 먼저지만, 이 프로젝트는 **기계를 항상 먼저** 두는 것이 규칙이다.
 *   ref를 따라 순서를 되돌리지 말 것.
 */
const UtComparisonReportDetailPage = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const state = { ...location.state };
  const { setSubTitle } = usePageSubTitle();

  const [searchParams] = useSearchParams();
  const [locationState, setLocationState] = useState<PageLocationState>(location.state);
  const [rptId, setRptId] = useState<string>(
    locationState?.rptId || searchParams?.get('rptId') || ''
  );
  const [rptNm, setRptNm] = useState<string>(
    locationState?.rptNm || searchParams?.get('rptNm') || ''
  );
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);

  const tabMenus = useMemo(
    () => [
      { id: 'MACH', name: t('ut.label.기계', '기계') },
      { id: 'ELTR', name: t('ut.label.전기', '전기') },
      { id: 'RPT', name: t('ut.label.분석 Report', '분석 Report') },
    ],
    [t]
  );

  const openChatbot = useChatbotStore((s) => s.openPanel);

  useEffect(() => {
    if (state?.rptId) {
      setLocationState(state);
    }
  }, [location]);

  useEffect(() => {
    const id = locationState?.rptId || searchParams?.get('rptId') || '';
    if (id) {
      setRptId(id);
      const nm = locationState?.rptNm || searchParams?.get('rptNm') || '';
      if (nm) {
        setRptNm(nm);
      } else {
        getUtMatrixComparisonReportList({}).then((result: UtMatrixComparisonReportList[]) => {
          setRptNm((result || []).find((o) => o.rptId === id)?.rptNm || '');
        });
      }
    }
  }, [locationState]);

  useEffect(() => {
    if (rptNm) setSubTitle(rptNm);
  }, [rptNm, setSubTitle]);

  if (!rptId) {
    return (
      <ContainerLayout>
        <div style={{ padding: 24, textAlign: 'center', color: '#666', fontSize: 12 }}>
          {t(
            'ut.label.조회할 리포트가 지정되지 않았습니다.',
            '조회할 리포트가 지정되지 않았습니다.'
          )}
        </div>
      </ContainerLayout>
    );
  }

  return (
    <ContainerLayout>
      <div css={tabs.wrap}>
        <Tabs
          value={currentTabIndex}
          onChange={(e, newValue) => setCurrentTabIndex(newValue)}
          css={tabs.tab}
        >
          {tabMenus.map((o, idx) => (
            <Tab key={o.id} label={o.name} id={`ut-rpt-tab-${idx}`} />
          ))}
        </Tabs>
      </div>
      {/*
        탭 전환 시 언마운트되면 스토어가 reset되고 다시 조회하게 되므로,
        두 화면을 모두 마운트해 두고 보이는 쪽만 표시한다.
      */}
      <div style={{ display: currentTabIndex === 0 ? 'block' : 'none' }}>
        <UtComparisonReportPage rptId={rptId} />
      </div>
      <div style={{ display: currentTabIndex === 1 ? 'block' : 'none' }}>
        <UtComparisonElecReportPage rptId={rptId} />
      </div>

      {/* 분석 Report 탭은 rptId로 SUM_M만 조회하므로 열릴 때 마운트해도 부담이 없다 */}
      {currentTabIndex === 2 && <AnalysisReportPanel rptId={rptId} rptNm={rptNm} />}

      <ChatbotButton onClick={() => openChatbot('ut-matrix')} />
    </ContainerLayout>
  );
};

export default UtComparisonReportDetailPage;
